import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import Button from './Button';

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  cta: string;
}

interface PricingCardWithSubscriptionProps {
  plan: PricingPlan;
  isPopular?: boolean;
  onTrialClick: () => void;
  onSubscribeClick?: () => void;
  index: number;
  className?: string;
}

const PricingCardWithSubscription: React.FC<PricingCardWithSubscriptionProps> = ({
  plan,
  isPopular = false,
  onTrialClick,
  onSubscribeClick,
  index,
  className = ''
}) => {
  const { name: title, price, period, description, features } = plan;
  const isProfessional = title === 'Professional';
  const isEnterprise = title === 'Enterprise';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1
      }}
      className={`group relative ${className}`}
    >
      {/* Badge "Populaire" minimaliste */}
      {isPopular && (
        <div className="absolute -top-3 left-6 z-10">
          <div className="bg-orange-500 text-white px-3 py-1 text-xs font-medium">
            Recommandé
          </div>
        </div>
      )}

      {/* Carte principale */}
      <div className={`relative bg-white border rounded-2xl transition-all duration-300 hover:shadow-lg ${
        isPopular 
          ? 'border-orange-500 shadow-sm' 
          : 'border-gray-200 hover:border-gray-300'
      }`}>
        
        {/* Header avec prix */}
        <div className="px-8 py-8 text-center border-b border-gray-100">
          <h3 className="text-xl font-medium text-gray-900 mb-6">
            {title}
          </h3>
          
          <div className="mb-4">
            <span className="text-4xl font-light text-gray-900">
              {price}
            </span>
            {period && (
              <span className="text-gray-500 text-lg font-light ml-1">
                {period}
              </span>
            )}
          </div>
          
          <p className="text-gray-600 text-base font-light">
            {description}
          </p>
        </div>

        {/* Contenu */}
        <div className="px-8 py-8">
          {/* Liste des fonctionnalités */}
          <div className="space-y-4 mb-8">
            {features.map((feature, featureIndex) => (
              <div
                key={featureIndex}
                className="flex items-start gap-4"
              >
                <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
                  <Check className="h-4 w-4 text-gray-600" strokeWidth={2} />
                </div>
                <span className="text-gray-700 text-base font-light leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>

          {/* Bouton */}
          <Button
            onClick={onTrialClick}
            className={`w-full font-medium transition-all duration-200 py-3.5 text-base ${
              isPopular 
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-gray-900 hover:bg-gray-800 text-white'
            }`}
          >
            {isEnterprise ? 'Contactez-nous' : 'Commencer l\'essai gratuit'}
          </Button>
          
          {/* Mention discrète sans CB */}
          {!isEnterprise && (
            <p className="text-xs text-gray-400 text-center mt-2 font-light">
              Aucune carte bancaire requise
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PricingCardWithSubscription;